import clsx from 'clsx';
import { createPortal } from 'react-dom';
import { useRef, useState, useEffect, useCallback, useLayoutEffect, type MouseEvent, type PointerEvent } from 'react';
import { getAiToolkit } from '@tiptap-pro/ai-toolkit';
import type { Editor } from '@tiptap/react';
import { AnimatePresence, motion } from 'motion/react';

import type { FeedbackDocumentKind } from '@/components/assistant-message-feedback';
import { useAgentSessionState } from '@/hooks/llm';
import type { ActiveBlock } from '@/hooks/llm/use-llm-editor-scope';
import { createTargetedEditTarget, subscribeAgentRunEvents, type AgentEditTarget } from '@/hooks/llm/use-llm-agent';
import { useFloatingChatSurface, type LauncherStatus, type ResizeEdge } from '@/hooks/use-floating-chat-surface';
import { useUserAccount } from '@/hooks/use-user-account';
import type { DocuAiDockColumn } from '@/state';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Tooltip } from '@flintmint/web-shared/components/tooltip';
import { Button } from '@flintmint/web-shared/components/button';
import { Chip } from '@flintmint/web-shared/components/chip';

import { useAutosizeTextArea } from '@flintmint/web-shared/hooks/use-autosize-textarea';
import { useTransitionMark } from '@flintmint/web-shared/hooks/use-transition-mark';
import { getChatMessagesKey, type ChatMessage as ChatMessageType } from '@flintmint/web-shared/utils/llm-message-utils';

import { animateOpacity, animateScaleAndOpacity, emphasizedTransition } from '@flintmint/web-shared/constants/ui';
import { getNormalizedLlmSelectionRange } from '@/utils/editor-utils';

import { ChatMessage } from './chat-message';
import { EnergyPopover } from './energy';
import { isPausedForRecharge } from './energy/energy-view';

import LogoDark from '@/assets/icons/logo-dark.svg?react';

import styles from './llm-chat.module.scss';
import { Collapse } from '@flintmint/web-shared/components/collapse';

const chatBodyPresenceAnimation = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const RESIZE_EDGES: ResizeEdge[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

const LAUNCHER_LABELS: Record<LauncherStatus, string> = {
  default: 'AI agent at your service',
  working: 'Working on it...',
  ready: 'Ready',
  failed: 'Failed',
};

export interface LlmChatProps {
  requestId: string;
  documentKind?: FeedbackDocumentKind;
  chatMessages: ChatMessageType[];
  cancelAgentRequest: () => void;
  onSubmitPrompt: (userPrompt: string, options?: { editTarget?: AgentEditTarget }) => void | Promise<void>;
  onRetryNetworkError: () => void;
  editor: Editor | null;
  hasSelection: boolean;
  activeBlock?: ActiveBlock | null;
  /** Which right sidebar the launcher docks into; the block editor overlay has its own. */
  dockColumn?: DocuAiDockColumn;
}

export const LlmChat = (props: LlmChatProps) => {
  const {
    editor,
    requestId,
    activeBlock,
    chatMessages,
    hasSelection,
    onSubmitPrompt,
    cancelAgentRequest,
    onRetryNetworkError,
    documentKind = 'note',
    dockColumn = 'layout',
  } = props;

  const [prompt, setPrompt] = useState('');
  const [isSubmittingPrompt, setIsSubmittingPrompt] = useState(false);

  // A refused first request removes its turn from the chat; the prompt returns to the composer unless the user has typed since.
  // Relies on `onSubmitPrompt` resolving before the response (callers fire `sendChatMessage` unawaited), so the submit-time clear runs before the refusal arrives.
  useEffect(
    () =>
      subscribeAgentRunEvents((runEvent) => {
        const refusedPrompt = 'refusal' in runEvent.event ? runEvent.event.refusal?.prompt : undefined;
        if (runEvent.requestId !== requestId || !refusedPrompt) return;
        setPrompt((current) => (current === '' ? refusedPrompt : current));
      }),
    [requestId]
  );
  const chatMessagesKey = getChatMessagesKey(chatMessages);

  const { isLoading: isAgentLoading, currentStep, isNetworkError, isReviewing, activityState } = useAgentSessionState(requestId);
  const canCancel = isAgentLoading && !isReviewing;
  const { account } = useUserAccount();

  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  // The surface owns the transcript measurement: only it knows when the chat is at the height its `open`
  // state calls for, which is the only height a transcript can be measured against.
  const surface = useFloatingChatSurface({
    requestId,
    dockColumn,
    surfaceRef,
    isLoading: isAgentLoading,
    isNetworkError,
    activityState,
    messagesContainerRef,
    transcriptKey: chatMessagesKey,
  });
  const { open, setOpen, interaction, activeEdge, launcherStatus, isPresent } = surface;
  useTransitionMark(surfaceRef, 'present', isPresent);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const launcherOpenRef = useRef<HTMLButtonElement | null>(null);
  const collapseRequested = useRef(false);
  const { isUserScrolledUp, onMessagesScroll, takeSavedScroll } = useMessagesScrollState(
    messagesContainerRef,
    open,
    chatMessagesKey
  );

  const textareaRef = useAutosizeTextArea(prompt, { maxHeight: 154, open });

  const launcherLabel = LAUNCHER_LABELS[launcherStatus];
  const hasPrompt = prompt.trim().length > 0;
  const canSubmitPrompt = hasPrompt && !isAgentLoading && !isSubmittingPrompt;
  const hasContext = hasSelection || !!activeBlock;
  const contextChipLabel = hasSelection ? 'Current Text Selection' : `Block - ${activeBlock?.name ?? ''}`;
  const contextChipColor = hasSelection ? 'secondary' : 'hero';
  const contextChipIcon = hasSelection ? 'text-selection' : 'blockchain-01';

  useEffect(() => {
    if (open) {
      textareaRef.current?.focus();
      return;
    }

    if (collapseRequested.current) {
      collapseRequested.current = false;
      launcherOpenRef.current?.focus();
    }
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;

    // Restore the collapse-time offset only for an unchanged transcript; anything new must come into view.
    const saved = takeSavedScroll();
    if (saved && saved.messagesKey === chatMessagesKey && !isNetworkError && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = saved.scrollTop;
      return;
    }

    if (saved || !isUserScrolledUp || isNetworkError) {
      messagesEndRef.current?.scrollIntoView({ block: 'end' });
    }
  }, [open, isUserScrolledUp, isNetworkError, chatMessagesKey, takeSavedScroll]);

  // The pointer leaves the thin handle mid-gesture, so the cursor is applied document-wide until release.
  useEffect(() => {
    if (interaction === 'idle') return;

    const cursor =
      interaction === 'dragging'
        ? 'grabbing'
        : getComputedStyle(surfaceRef.current ?? document.body).getPropertyValue(`--cursor-${activeEdge}`);
    const previous = {
      cursor: document.body.style.cursor,
      userSelect: document.body.style.userSelect,
      webkitUserSelect: document.body.style.webkitUserSelect,
    };
    document.body.style.cursor = cursor.trim();
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';

    return () => {
      document.body.style.cursor = previous.cursor;
      document.body.style.userSelect = previous.userSelect;
      document.body.style.webkitUserSelect = previous.webkitUserSelect;
    };
  }, [interaction, activeEdge]);

  // Suppression is a pointer concern: the flag survives a drag that ends without a click, so a
  // synthesized click (`detail === 0`; a pointer click always reports 1 or more) clears it and opens
  // instead of being swallowed.
  const openChat = (event: MouseEvent<HTMLButtonElement>) => {
    if (event.detail === 0) {
      surface.clearSuppressedClick();
    } else if (surface.consumeSuppressedClick()) {
      return;
    }

    setOpen(true);
  };

  // Collapsing unmounts the header with its Collapse button, so focus has to be handed back to the
  // launcher. Gated on the user asking for it: the chat also closes programmatically (the other
  // instance taking over, or a fresh mount), and those must not pull focus out of wherever it is.
  const collapseChat = () => {
    collapseRequested.current = true;
    setOpen(false);
  };

  const stopPointerDown = (event: PointerEvent<HTMLElement>) => event.stopPropagation();
  // The grip only moves: its click must not open the chat, and it consumes the post-drag suppression.
  const onGripClick = () => {
    surface.consumeSuppressedClick();
  };

  const submitPrompt = async () => {
    if (canSubmitPrompt) {
      const submittedPrompt = prompt;
      let editTarget: AgentEditTarget | undefined;

      if (hasSelection && editor) {
        const { from, to } = getNormalizedLlmSelectionRange(editor);
        editor.commands.setTextSelection({ from, to });
        getAiToolkit(editor).setActiveSelection(editor.state.selection);
        editTarget = createTargetedEditTarget({ from, to });
      } else if (activeBlock) {
        editTarget = createTargetedEditTarget(activeBlock.contentRange);
      }

      // Promise chain rather than try/finally: React Compiler bails out of the whole component on
      // try/finally. The call goes inside the chain so a synchronous throw is caught too and the
      // submitting flag is always cleared — otherwise the composer would stay disabled for good.
      setIsSubmittingPrompt(true);
      await Promise.resolve()
        .then(() => onSubmitPrompt(submittedPrompt.trim(), editTarget ? { editTarget } : undefined))
        .then(() => setPrompt((currentPrompt) => (currentPrompt === submittedPrompt ? '' : currentPrompt)))
        .catch((error) => console.error('Failed to submit prompt:', error))
        .finally(() => setIsSubmittingPrompt(false));
    }
  };

  return createPortal(
    <motion.div
      ref={surfaceRef}
      data-testid="llm-chat-panel"
      // The note and block chats render the same panel; e2e needs to tell the two apart.
      data-document-kind={documentKind}
      data-launcher-status={open ? undefined : launcherStatus}
      className={clsx(styles.surface, {
        [styles.chatOpen]: open,
        [styles.dragging]: interaction === 'dragging',
        [styles.resizing]: interaction === 'resizing',
      })}
      style={surface.style}
      // The note and block chats are separate conversations that swap when the block editor opens/closes;
      // the fade turns that swap into a transition, and the outgoing surface is inert while it fades.
      // The fade is a CSS transition keyed on `data-present` rather than a motion animation: motion's WAAPI
      // opacity animation makes Chrome present stale frames next to the overlay's `backdrop-filter` (DM-1559).
      // Still a motion element, for the geometry motion values in `style`.
      inert={!isPresent}
    >
      {!open ? (
        <div className={styles.launcher}>
          <button
            ref={launcherOpenRef}
            type="button"
            aria-label="Open DocuAI chat"
            data-testid="llm-chat-launcher"
            className={styles.launcherOpen}
            onClick={openChat}
            onPointerDown={surface.clearSuppressedClick}
          >
            <span className={styles.launcherLogo}>
              <LogoDark className={styles.launcherLogoIcon} />
            </span>
            {/* The button is named by its aria-label, so this copy would only be read twice over. */}
            <span aria-hidden="true" className={styles.launcherText}>
              <span className={styles.launcherTitle}>DocuAI</span>
              <span data-testid="llm-chat-launcher-status" className={styles.launcherSubtitle}>
                {launcherLabel}
              </span>
            </span>
          </button>
          <Tooltip content="Move chat">
            <button
              type="button"
              aria-label="Move chat"
              data-testid="launcher-move-btn"
              className={styles.launcherGrip}
              onClick={onGripClick}
              {...surface.getMoveHandleProps()}
            >
              <Icon type="drag-drop-vertical-solid" size={24} />
            </button>
          </Tooltip>
        </div>
      ) : (
        <div className={styles.chatHeader}>
          <div className={styles.chatBrandLogo}>
            <LogoDark className={styles.chatBrandLogoIcon} />
          </div>
          <div className={styles.chatBrandTitle}>DocuAI</div>

          <div data-testid="chat-header-actions" className={styles.chatHeaderActions}>
            <Tooltip content="Collapse chat">
              <button
                type="button"
                aria-label="Collapse chat"
                data-testid="chat-collapse-btn"
                className={styles.chatHeaderButton}
                onPointerDown={stopPointerDown}
                onClick={collapseChat}
              >
                <Icon type="arrow-down-01" size={24} />
              </button>
            </Tooltip>
            <Tooltip content="Move chat">
              <button
                type="button"
                aria-label="Move chat"
                data-testid="chat-move-btn"
                className={clsx(styles.chatHeaderButton, styles.chatMoveButton)}
                {...surface.getMoveHandleProps()}
              >
                <Icon type="drag-drop-vertical-solid" size={24} />
              </button>
            </Tooltip>
          </div>
        </div>
      )}

      {/* Outside the branch above on purpose: the region has to outlive the collapse so the label lands
          as a content change, and it sits outside the Open button, whose presentational children — a
          nested live region among them — are pruned from the accessibility tree. */}
      <div role="status" className={styles.launcherStatusLive}>
        {open ? '' : launcherLabel}
      </div>

      <AnimatePresence initial={false} mode="popLayout">
        {open && (
          <motion.div key="body" className={styles.chatBody} {...chatBodyPresenceAnimation} transition={emphasizedTransition}>
            {!chatMessages.length ? (
              <div data-testid="chat-empty-state" className={styles.chatEmptyState}>
                How can I help?
              </div>
            ) : (
              <div
                data-testid="chat-messages"
                className={styles.chatMessages}
                ref={messagesContainerRef}
                onScroll={onMessagesScroll}
              >
                {chatMessages.map((message) => {
                  return <ChatMessage key={message.id} message={message} requestId={requestId} documentKind={documentKind} />;
                })}
                {isNetworkError && (
                  <div data-testid="chat-network-error" className={styles.chatOffline}>
                    <div className={styles.chatOfflineCard}>
                      <div className={styles.chatOfflineIcon}>
                        <Icon type="wifi-error-02" size={20} multiColor />
                      </div>
                      <div className={styles.chatOfflineText}>
                        <div className={styles.chatOfflineTitle}>Lost connection to the internet</div>
                        <div className={styles.chatOfflineSubtitle}>
                          Make sure your internet connection is restored and try again.
                        </div>
                      </div>
                    </div>
                    <Button itemsDirection="end" onClick={onRetryNetworkError} icon={<Icon size={18} type="refresh" />}>
                      Try again
                    </Button>
                  </div>
                )}
                <AnimatePresence>
                  {isAgentLoading && currentStep && (
                    <motion.div
                      key="step-indicator"
                      className={styles.chatStepIndicator}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className={styles.chatStepIcon}>
                        <Icon type="loading-03" size={14} className={styles.chatStepSpinner} />
                      </div>
                      <span className={styles.chatStepText}>{currentStep}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
                <div ref={messagesEndRef} />
              </div>
            )}

            <div className={styles.chatPromptWrapper}>
              <div className={styles.chatPrompt}>
                <Collapse open={hasContext}>
                  <AnimatePresence initial={false}>
                    {hasContext && (
                      <motion.div key="chat-context" {...animateOpacity} className={styles.chatContext}>
                        <span>{hasContext ? 'Working with' : 'Starting from'}</span>
                        <Chip
                          data-testid="chat-context-chip"
                          color={contextChipColor}
                          label={contextChipLabel}
                          onClose={() => {
                            if (editor) {
                              editor.chain().setTextSelection(editor.state.selection.from).focus().run();
                              getAiToolkit(editor).setActiveSelection(null);
                            }
                          }}
                          icon={<Icon size={14} type={contextChipIcon} />}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Collapse>
                <textarea
                  ref={textareaRef}
                  placeholder={composerPlaceholder(isPausedForRecharge(account), isAgentLoading)}
                  value={prompt}
                  rows={1}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void submitPrompt();
                    }
                  }}
                />

                <div className={styles.chatPromptActions}>
                  <EnergyPopover />
                  <button disabled className={styles.chatPromptActionButton}>
                    <Icon type="plus" size={18} />
                  </button>
                  <Tooltip content={isAgentLoading && canCancel ? 'Stop AI' : ''}>
                    <button
                      onClick={isAgentLoading && canCancel ? cancelAgentRequest : submitPrompt}
                      disabled={isAgentLoading ? !canCancel : !hasPrompt || isSubmittingPrompt}
                      className={clsx(styles.chatPromptActionButton, styles.chatPromptSubmit, {
                        [styles.active]: canSubmitPrompt,
                      })}
                      data-loading={isAgentLoading}
                      data-testid={isAgentLoading && canCancel ? 'chat-prompt-cancel-btn' : 'chat-prompt-btn'}
                    >
                      <AnimatePresence mode="wait" initial={false}>
                        {!(isAgentLoading && canCancel) ? (
                          <motion.div key="prompt-icon" {...animateScaleAndOpacity}>
                            <Icon type="arrow-up-02" size={18} />
                          </motion.div>
                        ) : (
                          <motion.div key="prompt-cancel-icon" {...animateScaleAndOpacity}>
                            <Icon size={18} type="cancel-01" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {open &&
        RESIZE_EDGES.map((edge) => (
          <div
            key={edge}
            aria-hidden
            data-testid={`chat-resize-${edge}`}
            className={clsx(styles.resizeHandle, styles[`resize-${edge}`])}
            {...surface.getResizeHandleProps(edge)}
          />
        ))}
    </motion.div>,
    document.body
  );
};

/** Tracks whether the user scrolled up, and remembers the scroll offset (with its transcript) at collapse. */
const useMessagesScrollState = (
  messagesContainerRef: { current: HTMLDivElement | null },
  open: boolean,
  chatMessagesKey: string
) => {
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false);
  const savedScroll = useRef<{ scrollTop: number; messagesKey: string } | null>(null);

  // Captured at the collapse commit: the transcript stays mounted for its exit animation, and chunks
  // streamed in that window must not be tagged onto the pre-collapse offset.
  const wasOpen = useRef(open);
  useLayoutEffect(() => {
    const collapsed = wasOpen.current && !open;
    wasOpen.current = open;

    const container = messagesContainerRef.current;
    if (collapsed && container) {
      savedScroll.current = { scrollTop: container.scrollTop, messagesKey: chatMessagesKey };
    }
  }, [open, chatMessagesKey, messagesContainerRef]);

  const takeSavedScroll = useCallback(() => {
    const saved = savedScroll.current;
    savedScroll.current = null;
    return saved;
  }, []);

  const onMessagesScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const { scrollHeight, scrollTop, clientHeight } = container;
    const isScrollable = scrollHeight > clientHeight + 1;

    if (!isScrollable) {
      return setIsUserScrolledUp(false);
    }

    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const isScrolledUp = distanceFromBottom > 24;

    if (isScrolledUp !== isUserScrolledUp) {
      setIsUserScrolledUp(isScrolledUp);
    }
  };

  return { isUserScrolledUp, onMessagesScroll, takeSavedScroll };
};

const composerPlaceholder = (pausedForRecharge: boolean, isAgentLoading: boolean) => {
  if (!pausedForRecharge) return 'Ask AI anything';
  return isAgentLoading ? 'Finishing your request\u2026' : 'AI paused. Recharge to continue.';
};
